"use client";

import * as React from "react";
import Script from "next/script";
import { cn } from "@/app/_lib/utils";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: Record<string, unknown>,
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      execute: (widgetId: string, options?: Record<string, unknown>) => void;
    };
  }
}

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export interface TurnstileWidgetHandle {
  /** Fetch a fresh token — call after a failed submit so the (single-use)
   * previous token doesn't get resent on retry. */
  reset: () => void;
}

export interface TurnstileWidgetProps {
  /** Called with the verification token once Cloudflare issues one, and with
   * "" if it expires or errors out. */
  onVerify: (token: string) => void;
  className?: string;
  /**
   * "interaction-only" (default): reserves a slot in the page that Cloudflare
   * may or may not show a widget in, used on the standalone contact/review/
   * booking forms where a little reserved space is fine.
   *
   * "invisible": zero footprint in the layout — no reserved space, nothing
   * rendered under normal traffic. Runs the check silently in the background
   * via `turnstile.execute()` and only ever surfaces a visible challenge if
   * Cloudflare's risk engine specifically flags the visitor (rare, and by
   * design a fallback — this is the same model as reCAPTCHA v3). Use this
   * anywhere a reserved widget slot would visually crowd the UI, e.g. inside
   * a chat window.
   */
  mode?: "interaction-only" | "invisible";
}

/**
 * Reusable Cloudflare Turnstile widget.
 *
 * `mode="invisible"` requires a *separate* site key created in the Cloudflare
 * dashboard with Widget Mode set to "Invisible" (NEXT_PUBLIC_TURNSTILE_INVISIBLE_SITE_KEY
 * / TURNSTILE_INVISIBLE_SECRET_KEY on the server side). A "Managed"-mode key —
 * used everywhere else via the default `mode="interaction-only"` — can still
 * decide on its own, per visitor, to show an interactive challenge regardless
 * of the `appearance`/`execution` params passed here; only a key actually
 * configured as "Invisible" guarantees zero visible UI. Widget Mode lives on
 * the site key itself and can't be overridden client-side.
 *
 * Note for local dev: Cloudflare's published dummy *testing* site keys (see
 * SETUP.md) always render a visible widget by design, regardless of `mode` —
 * that's how you can visually confirm it's wired up.
 */
export const TurnstileWidget = React.forwardRef<
  TurnstileWidgetHandle,
  TurnstileWidgetProps
>(function TurnstileWidget({ onVerify, className, mode = "interaction-only" }, ref) {
  const containerId = React.useId().replace(/[:]/g, "");
  const widgetIdRef = React.useRef<string | null>(null);
  const onVerifyRef = React.useRef(onVerify);
  onVerifyRef.current = onVerify;

  const siteKey =
    mode === "invisible"
      ? process.env.NEXT_PUBLIC_TURNSTILE_INVISIBLE_SITE_KEY
      : process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const renderWidget = React.useCallback(() => {
    if (!siteKey || !window.turnstile || widgetIdRef.current) return;
    widgetIdRef.current = window.turnstile.render(`#${containerId}`, {
      sitekey: siteKey,
      appearance: mode === "invisible" ? "execute" : "interaction-only",
      // "appearance" only controls visual footprint — it's "execution" that
      // controls when the challenge actually starts running, and it
      // defaults to "render" (starts immediately on its own). Without this
      // explicit "execute", the widget was already auto-running before our
      // manual turnstile.execute() call below ever fired, so that call was
      // racing/duplicating the auto-run instead of being the real trigger.
      execution: mode === "invisible" ? "execute" : "render",
      // Every form on this site is light-themed — without this, Turnstile
      // defaults to "auto" and follows the visitor's OS/browser dark-mode
      // preference, which is why it can render as a jarring dark widget on
      // an otherwise light form.
      theme: "light",
      callback: (token: string) => onVerifyRef.current(token),
      "expired-callback": () => onVerifyRef.current(""),
      "error-callback": () => onVerifyRef.current(""),
    });
    if (mode === "invisible" && widgetIdRef.current) {
      window.turnstile.execute(widgetIdRef.current);
    }
  }, [containerId, siteKey, mode]);

  React.useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        if (mode === "invisible") {
          window.turnstile.execute(widgetIdRef.current);
        }
      }
    },
  }));

  React.useEffect(() => {
    if (window.turnstile) renderWidget();
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [renderWidget]);

  if (!siteKey) return null;

  if (mode === "invisible") {
    // Deliberately NOT display:none. "execute" appearance already keeps
    // this at zero footprint under normal traffic, but Cloudflare's risk
    // engine can still decide a given visitor needs an interactive
    // challenge — when that happens it needs to render a real, visible,
    // clickable iframe into this container. display:none would make that
    // fallback impossible to complete: the challenge silently stalls,
    // error-callback/timeout fires with an empty token, and every submit
    // gets rejected server-side even though the user never saw anything to
    // respond to. Fixed + off-screen-when-empty keeps normal-traffic layout
    // untouched while still letting a flagged challenge become visible.
    return (
      <>
        <Script src={SCRIPT_SRC} strategy="afterInteractive" onLoad={renderWidget} />
        <div
          id={containerId}
          className="fixed bottom-4 right-4 z-[9999]"
        />
      </>
    );
  }

  return (
    <>
      <Script
        src={SCRIPT_SRC}
        strategy="afterInteractive"
        onLoad={renderWidget}
      />
      {/* my-4: breathing room from whatever sits above/below it in every
          form — without this the widget visually crowds adjacent
          checkboxes/buttons since it renders with zero margin of its own. */}
      <div id={containerId} className={cn("my-4", className)} />
    </>
  );
});
